import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { 
  Calendar, 
  Plus, 
  Edit, 
  Trash2, 
  Users, 
  Euro,
  Star,
  ChefHat,
  Clock,
  MapPin,
  Crown,
  TrendingUp
} from "lucide-react";
import axios from "axios";
import ChefSubscriptionPlans from "./ChefSubscriptionPlans";
import MarketingTools from "./MarketingTools";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ChefDashboard = ({ user, onClose }) => {
  const [myWorkshops, setMyWorkshops] = useState([]);
  const [myBookings, setMyBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showSubscriptionPlans, setShowSubscriptionPlans] = useState(false);
  const [showMarketingTools, setShowMarketingTools] = useState(false);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [uploadedMedia, setUploadedMedia] = useState([]);
  const [chefProfile, setChefProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [uploadingProfileImage, setUploadingProfileImage] = useState(false);
  const [newWorkshop, setNewWorkshop] = useState({
    title: "",
    description: "",
    location: "",
    address: "",
    price: "",
    duration: "",
    max_participants: "",
    category: "",
    materials_included: true,
    media_urls: []
  });

  useEffect(() => {
    loadChefData();
  }, []);

  const loadChefData = async () => {
    try {
      // In a real app, we'd filter by chef ID
      const [workshopsRes, bookingsRes] = await Promise.all([
        axios.get(`${API}/workshops`),
        axios.get(`${API}/bookings`)
      ]);

      // Filter workshops by current chef (in real app, backend would do this)
      const chefWorkshops = workshopsRes.data.filter(w => w.chef_name === user.name);
      setMyWorkshops(chefWorkshops);
      setMyBookings(bookingsRes.data);

      // Load chef profile
      await loadChefProfile();

      // Load current subscription
      try {
        const token = localStorage.getItem('token');
        const subResponse = await axios.get(`${API}/chef/subscription`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCurrentSubscription(subResponse.data);
      } catch (error) {
        // User has no subscription (free plan)
        setCurrentSubscription({ 
          plan_name: "Plan Gratuit", 
          commission_rate: 0.20,
          status: "free"
        });
      }
    } catch (error) {
      console.error("Error loading chef data:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadChefProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/chef/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChefProfile(response.data);
    } catch (error) {
      console.error("Error loading chef profile:", error);
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setProfileLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(`${API}/chef/profile`, chefProfile, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setChefProfile(response.data);
      alert("Profil mis à jour avec succès !");
    } catch (error) {
      console.error("Error updating profile:", error);
      alert("Erreur lors de la mise à jour du profil");
    } finally {
      setProfileLoading(false);
    }
  };

  const handleProfileImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploadingProfileImage(true);
    
    try {
      const formData = new FormData();
      formData.append('files', file);

      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/upload/media`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      const uploadedFile = response.data.uploaded_files[0];
      setChefProfile(prev => ({
        ...prev,
        image_url: uploadedFile.url
      }));
      
    } catch (error) {
      console.error("Error uploading profile image:", error);
      alert("Erreur lors de l'upload de l'image");
    } finally {
      setUploadingProfileImage(false);
    }
  };

  const handleCreateWorkshop = async (e) => {
    e.preventDefault();
    try {
      const workshopData = {
        ...newWorkshop,
        chef_name: user.name,
        price: parseFloat(newWorkshop.price),
        max_participants: parseInt(newWorkshop.max_participants)
      };

      // Add uploaded media URLs
      if (uploadedMedia.length > 0) {
        workshopData.media_urls = uploadedMedia.map(media => media.url);
        workshopData.image_url = uploadedMedia[0].url; // Use first image as main image
      }

      const token = localStorage.getItem('token');
      await axios.post(`${API}/workshops`, workshopData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Reset form and reload data
      setNewWorkshop({
        title: "",
        description: "",
        location: "",
        address: "",
        price: "",
        duration: "",
        max_participants: "",
        category: "",
        materials_included: true,
        media_urls: []
      });
      setUploadedMedia([]);
      setShowCreateForm(false);
      loadChefData();
    } catch (error) {
      console.error("Error creating workshop:", error);
    }
  };

  const handleMediaUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    setUploadingMedia(true);
    
    try {
      const formData = new FormData();
      files.forEach(file => {
        formData.append('files', file);
      });

      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/upload/media`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      const newMedia = response.data.uploaded_files;
      setUploadedMedia(prev => [...prev, ...newMedia]);
      
    } catch (error) {
      console.error("Error uploading media:", error);
      alert("Erreur lors de l'upload des fichiers");
    } finally {
      setUploadingMedia(false);
    }
  };

  const removeMedia = (index) => {
    setUploadedMedia(prev => prev.filter((_, i) => i !== index));
  };

  const getMyStats = () => {
    const totalRevenue = myBookings.reduce((sum, booking) => sum + (booking.total_price || 0), 0);
    const totalParticipants = myBookings.reduce((sum, booking) => sum + (booking.participants_count || 0), 0);
    
    return {
      totalWorkshops: myWorkshops.length,
      totalBookings: myBookings.length,
      totalRevenue,
      totalParticipants
    };
  };

  const stats = getMyStats();

  if (showMarketingTools) {
    return (
      <div className="min-h-screen bg-gray-100">
        <div className="bg-white shadow-sm border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <h1 className="text-2xl font-bold text-orange-600">Outils Marketing</h1>
              <Button variant="outline" onClick={() => setShowMarketingTools(false)}>
                Retour au Dashboard
              </Button>
            </div>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <MarketingTools user={user} currentSubscription={currentSubscription} />
        </div>
      </div>
    );
  }

  if (showSubscriptionPlans) {
    return <ChefSubscriptionPlans user={user} onClose={() => setShowSubscriptionPlans(false)} />;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600 mx-auto mb-4"></div>
          <p>Chargement de votre espace formateur...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div>
              <h1 className="text-2xl font-bold text-orange-600">Espace Formateur</h1>
              <p className="text-sm text-gray-600">Gérez vos ateliers et réservations</p>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-600">
                Bienvenue <strong>{user.name}</strong>
              </span>
              <Button variant="outline" onClick={onClose}>
                Retour au site
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Mes Ateliers</CardTitle>
              <ChefHat className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalWorkshops}</div>
              <p className="text-xs text-muted-foreground">
                Ateliers créés
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Réservations</CardTitle>
              <Calendar className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalBookings}</div>
              <p className="text-xs text-muted-foreground">
                Réservations reçues
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Participants</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalParticipants}</div>
              <p className="text-xs text-muted-foreground">
                Personnes formées
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Revenus</CardTitle>
              <Euro className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalRevenue}€</div>
              <p className="text-xs text-muted-foreground">
                Commission: {currentSubscription ? (currentSubscription.commission_rate * 100).toFixed(0) : '20'}%
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Subscription Status */}
        {currentSubscription && (
          <Card className="mb-8 bg-gradient-to-r from-orange-500 to-red-500 text-white">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold flex items-center">
                    <Crown className="w-5 h-5 mr-2" />
                    {currentSubscription.plan_name}
                  </h3>
                  <p className="text-orange-100">
                    Commission: {(currentSubscription.commission_rate * 100).toFixed(0)}%
                    {currentSubscription.status === 'free' ? ' (Plan Gratuit)' : ''}
                  </p>
                </div>
                <div className="text-right">
                  <Button 
                    variant="secondary"
                    onClick={() => setShowSubscriptionPlans(true)}
                    className="bg-white text-orange-600 hover:bg-orange-50 mr-2"
                  >
                    {currentSubscription.status === 'free' ? 'Upgrader' : 'Gérer'}
                  </Button>
                  <Button 
                    variant="secondary"
                    onClick={() => setShowMarketingTools(true)}
                    className="bg-white text-purple-600 hover:bg-purple-50"
                  >
                    🎯 Marketing
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <Tabs defaultValue="workshops" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="workshops">Mes Ateliers</TabsTrigger>
            <TabsTrigger value="bookings">Réservations</TabsTrigger>
            <TabsTrigger value="profile">Mon Profil</TabsTrigger>
          </TabsList>

          <TabsContent value="workshops" className="mt-6">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle>Mes Ateliers Culinaires</CardTitle>
                    <CardDescription>
                      Gérez vos ateliers et créez-en de nouveaux
                    </CardDescription>
                  </div>
                  <Button 
                    onClick={() => setShowCreateForm(!showCreateForm)}
                    className="bg-orange-600 hover:bg-orange-700"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Nouvel Atelier
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {showCreateForm && (
                  <Card className="mb-6">
                    <CardHeader>
                      <CardTitle>Créer un Nouvel Atelier</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <form onSubmit={handleCreateWorkshop} className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="title">Titre de l'atelier</Label>
                            <Input
                              id="title"
                              value={newWorkshop.title}
                              onChange={(e) => setNewWorkshop({...newWorkshop, title: e.target.value})}
                              placeholder="Ex: Cuisine Italienne Traditionnelle"
                              required
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="category">Catégorie</Label>
                            <Input
                              id="category"
                              value={newWorkshop.category}
                              onChange={(e) => setNewWorkshop({...newWorkshop, category: e.target.value})}
                              placeholder="Ex: Italienne, Pâtisserie"
                              required
                            />
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="description">Description</Label>
                          <Textarea
                            id="description"
                            value={newWorkshop.description}
                            onChange={(e) => setNewWorkshop({...newWorkshop, description: e.target.value})}
                            placeholder="Décrivez votre atelier en détail..."
                            required
                          />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="location">Ville</Label>
                            <Input
                              id="location"
                              value={newWorkshop.location}
                              onChange={(e) => setNewWorkshop({...newWorkshop, location: e.target.value})}
                              placeholder="Ex: Paris 11e"
                              required
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="address">Adresse complète</Label>
                            <Input
                              id="address"
                              value={newWorkshop.address}
                              onChange={(e) => setNewWorkshop({...newWorkshop, address: e.target.value})}
                              placeholder="123 rue de la Cuisine, 75011 Paris"
                              required
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="price">Prix par personne (€)</Label>
                            <Input
                              id="price"
                              type="number"
                              value={newWorkshop.price}
                              onChange={(e) => setNewWorkshop({...newWorkshop, price: e.target.value})}
                              placeholder="75"
                              required
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="duration">Durée</Label>
                            <Input
                              id="duration"
                              value={newWorkshop.duration}
                              onChange={(e) => setNewWorkshop({...newWorkshop, duration: e.target.value})}
                              placeholder="Ex: 3h, 2h30"
                              required
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="max_participants">Participants max</Label>
                            <Input
                              id="max_participants"
                              type="number"
                              value={newWorkshop.max_participants}
                              onChange={(e) => setNewWorkshop({...newWorkshop, max_participants: e.target.value})}
                              placeholder="8"
                              required
                            />
                          </div>
                        </div>

                        <div className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            id="materials_included"
                            checked={newWorkshop.materials_included}
                            onChange={(e) => setNewWorkshop({...newWorkshop, materials_included: e.target.checked})}
                          />
                          <Label htmlFor="materials_included">Matériel et ingrédients inclus</Label>
                        </div>

                        {/* Media Upload Section */}
                        <div className="space-y-4">
                          <div>
                            <Label htmlFor="media-upload">Photos et Vidéos de l'Atelier</Label>
                            <div className="mt-2">
                              <input
                                id="media-upload"
                                type="file"
                                multiple
                                accept="image/*,video/*"
                                onChange={handleMediaUpload}
                                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-orange-50 file:text-orange-700 hover:file:bg-orange-100"
                                disabled={uploadingMedia}
                              />
                              <p className="text-xs text-gray-500 mt-1">
                                Formats acceptés: JPG, PNG, GIF, MP4, MOV. Max 10 fichiers.
                              </p>
                            </div>
                          </div>

                          {/* Upload Progress */}
                          {uploadingMedia && (
                            <div className="flex items-center space-x-2 text-orange-600">
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-orange-600"></div>
                              <span className="text-sm">Upload en cours...</span>
                            </div>
                          )}

                          {/* Media Preview */}
                          {uploadedMedia.length > 0 && (
                            <div className="space-y-2">
                              <Label>Médias uploadés :</Label>
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                {uploadedMedia.map((media, index) => (
                                  <div key={index} className="relative">
                                    {media.type.startsWith('image/') ? (
                                      <img
                                        src={`${BACKEND_URL}${media.url}`}
                                        alt={media.filename}
                                        className="w-full h-20 object-cover rounded-lg border"
                                      />
                                    ) : (
                                      <video
                                        src={`${BACKEND_URL}${media.url}`}
                                        className="w-full h-20 object-cover rounded-lg border"
                                        controls={false}
                                      />
                                    )}
                                    <button
                                      type="button"
                                      onClick={() => removeMedia(index)}
                                      className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full text-xs hover:bg-red-600"
                                    >
                                      ×
                                    </button>
                                    <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs p-1 rounded-b-lg truncate">
                                      {media.filename}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="flex space-x-2">
                          <Button type="submit" className="bg-orange-600 hover:bg-orange-700">
                            Créer l'Atelier
                          </Button>
                          <Button type="button" variant="outline" onClick={() => setShowCreateForm(false)}>
                            Annuler
                          </Button>
                        </div>
                      </form>
                    </CardContent>
                  </Card>
                )}

                <div className="space-y-4">
                  {myWorkshops.length > 0 ? myWorkshops.map((workshop) => (
                    <div key={workshop.id} className="flex items-start justify-between p-4 border rounded-lg">
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg">{workshop.title}</h3>
                        <p className="text-gray-600 mb-2">{workshop.description}</p>
                        <div className="flex items-center space-x-4 text-sm text-gray-500">
                          <div className="flex items-center">
                            <MapPin className="w-4 h-4 mr-1" />
                            {workshop.location}
                          </div>
                          <div className="flex items-center">
                            <Clock className="w-4 h-4 mr-1" />
                            {workshop.duration}
                          </div>
                          <div className="flex items-center">
                            <Users className="w-4 h-4 mr-1" />
                            Max {workshop.max_participants}
                          </div>
                          <div className="flex items-center">
                            <Euro className="w-4 h-4 mr-1" />
                            {workshop.price}€
                          </div>
                        </div>
                        <div className="mt-2">
                          <Badge>{workshop.category}</Badge>
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <Button variant="outline" size="sm">
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="outline" size="sm">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  )) : (
                    <div className="text-center py-12">
                      <ChefHat className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-500 mb-4">Vous n'avez pas encore créé d'atelier</p>
                      <Button 
                        onClick={() => setShowCreateForm(true)}
                        className="bg-orange-600 hover:bg-orange-700"
                      >
                        Créer votre premier atelier
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="bookings" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Réservations de vos Ateliers</CardTitle>
                <CardDescription>
                  Suivez les inscriptions à vos ateliers
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {myBookings.length > 0 ? myBookings.map((booking) => (
                    <div key={booking.id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div>
                        <h3 className="font-semibold">{booking.user_name}</h3>
                        <p className="text-sm text-gray-600">{booking.user_email}</p>
                        <p className="text-xs text-gray-500">
                          {booking.participants_count} participant{booking.participants_count > 1 ? 's' : ''} • {booking.total_price}€
                        </p>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Badge 
                          variant={booking.status === 'confirmed' ? 'default' : 'secondary'}
                          className={booking.status === 'confirmed' ? 'bg-green-600' : ''}
                        >
                          {booking.status === 'confirmed' ? 'Confirmée' : booking.status}
                        </Badge>
                      </div>
                    </div>
                  )) : (
                    <div className="text-center py-12">
                      <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-500">Aucune réservation pour le moment</p>
                      <p className="text-sm text-gray-400">Les réservations apparaîtront ici quand des clients s'inscriront à vos ateliers</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="profile" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Mon Profil de Chef</CardTitle>
                <CardDescription>
                  Gérez votre profil public et présentez-vous aux clients
                </CardDescription>
              </CardHeader>
              <CardContent>
                {chefProfile ? (
                  <form onSubmit={handleProfileUpdate} className="space-y-6">
                    {/* Photo de profil */}
                    <div className="space-y-4">
                      <Label>Photo de profil</Label>
                      <div className="flex items-center space-x-6">
                        <div className="w-24 h-24 rounded-full overflow-hidden bg-gray-100 border-2 border-gray-200">
                          {chefProfile.image_url ? (
                            <img
                              src={`${BACKEND_URL}${chefProfile.image_url}`}
                              alt={chefProfile.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-gray-400">
                              <ChefHat className="w-8 h-8" />
                            </div>
                          )}
                        </div>
                        <div>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleProfileImageUpload}
                            className="hidden"
                            id="profile-image-upload"
                            disabled={uploadingProfileImage}
                          />
                          <Label 
                            htmlFor="profile-image-upload"
                            className="cursor-pointer inline-flex items-center px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
                          >
                            {uploadingProfileImage ? (
                              <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                Upload...
                              </>
                            ) : (
                              'Changer la photo'
                            )}
                          </Label>
                          <p className="text-xs text-gray-500 mt-1">
                            JPG, PNG ou GIF. Max 5MB.
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Informations de base */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="profile-name">Nom complet *</Label>
                        <Input
                          id="profile-name"
                          value={chefProfile.name || ''}
                          onChange={(e) => setChefProfile({...chefProfile, name: e.target.value})}
                          placeholder="Votre nom complet"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="profile-email">Email</Label>
                        <Input
                          id="profile-email"
                          type="email"
                          value={chefProfile.email || user.email}
                          disabled
                          className="bg-gray-100"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="profile-specialty">Spécialité culinaire *</Label>
                        <Input
                          id="profile-specialty"
                          value={chefProfile.specialty || ''}
                          onChange={(e) => setChefProfile({...chefProfile, specialty: e.target.value})}
                          placeholder="Ex: Cuisine Française, Pâtisserie..."
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="profile-location">Ville *</Label>
                        <Input
                          id="profile-location"
                          value={chefProfile.location || ''}
                          onChange={(e) => setChefProfile({...chefProfile, location: e.target.value})}
                          placeholder="Ex: Paris 11e, Lyon 2e..."
                          required
                        />
                      </div>
                    </div>

                    {/* Bio/Présentation */}
                    <div className="space-y-2">
                      <Label htmlFor="profile-bio">Présentation personnelle *</Label>
                      <Textarea
                        id="profile-bio"
                        value={chefProfile.bio || ''}
                        onChange={(e) => setChefProfile({...chefProfile, bio: e.target.value})}
                        placeholder="Présentez-vous ! Parlez de votre parcours, votre passion pour la cuisine, vos spécialités... Les clients adorent connaître l'histoire de leur chef !"
                        rows={5}
                        required
                        className="resize-none"
                      />
                      <p className="text-xs text-gray-500">
                        Cette présentation sera visible sur votre profil public. Soyez authentique et passionné !
                      </p>
                    </div>

                    {/* Actions */}
                    <div className="flex space-x-4 pt-4">
                      <Button 
                        type="submit" 
                        className="bg-orange-600 hover:bg-orange-700"
                        disabled={profileLoading}
                      >
                        {profileLoading ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                            Mise à jour...
                          </>
                        ) : (
                          'Enregistrer le profil'
                        )}
                      </Button>
                      <Button type="button" variant="outline">
                        Aperçu public
                      </Button>
                    </div>

                    {/* Statistiques du profil */}
                    <div className="border-t pt-6 mt-6">
                      <h3 className="text-lg font-semibold mb-4">Statistiques de votre profil</h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <Card className="p-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm text-gray-600">Note moyenne</p>
                              <p className="text-2xl font-bold text-orange-600">
                                {chefProfile.rating || '0.0'}
                              </p>
                            </div>
                            <Star className="w-8 h-8 text-yellow-400 fill-current" />
                          </div>
                        </Card>
                        <Card className="p-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm text-gray-600">Ateliers créés</p>
                              <p className="text-2xl font-bold text-blue-600">
                                {chefProfile.workshops_count || 0}
                              </p>
                            </div>
                            <Calendar className="w-8 h-8 text-blue-400" />
                          </div>
                        </Card>
                        <Card className="p-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm text-gray-600">Profil complété</p>
                              <p className="text-2xl font-bold text-green-600">
                                {((chefProfile.name ? 1 : 0) + 
                                  (chefProfile.specialty ? 1 : 0) + 
                                  (chefProfile.bio ? 1 : 0) + 
                                  (chefProfile.location ? 1 : 0) + 
                                  (chefProfile.image_url ? 1 : 0)) * 20}%
                              </p>
                            </div>
                            <TrendingUp className="w-8 h-8 text-green-400" />
                          </div>
                        </Card>
                      </div>
                      
                      {/* Conseils pour améliorer le profil */}
                      <div className="mt-6 p-4 bg-orange-50 rounded-lg border border-orange-200">
                        <h4 className="font-semibold text-orange-800 mb-2">💡 Conseils pour un profil attractif</h4>
                        <ul className="text-sm text-orange-700 space-y-1">
                          <li>• Ajoutez une photo souriante et professionnelle</li>
                          <li>• Racontez votre parcours culinaire dans la bio</li>
                          <li>• Mentionnez vos spécialités et formations</li>
                          <li>• Soyez authentique et passionné dans votre présentation</li>
                        </ul>
                      </div>
                    </div>
                  </form>
                ) : (
                  <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600 mx-auto mb-4"></div>
                    <p>Chargement de votre profil...</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default ChefDashboard;